import * as React from 'react';

// Listing banner — social ad creative, 1:1 (feed) and 9:16 (story/reel).
//
// Ported from the design handoff (design_handoff_listing_banner). Authored
// at 1080 wide; every value below is in canvas px at that width. To render
// smaller, scale the whole frame via `width` — never re-tune the internals,
// which is what breaks templates across sizes.
//
// Both ratios are ONE template, not two. They share the corner-anchored
// composition, the type scale and the brand kit; only the canvas height and
// four geometry values differ (see BANNER_GEOMETRY). Splitting them into
// separate components is what lets the two drift apart, which is exactly
// what the catalog cannot tolerate — Meta picks between them per placement,
// so a viewer can see both within one ad.
//
// Nothing here is hard-coded copy: imagery, price, currency, units, accent
// and logo all arrive as data.

export type BannerRatio = '1:1' | '9:16';

/** Canvas width. Fixed across ratios — only the height moves. */
export const BANNER_CANVAS = 1080;

export const BANNER_RATIOS: BannerRatio[] = ['1:1', '9:16'];

export const BANNER_RATIO_LABEL: Record<BannerRatio, string> = {
  '1:1': 'Square · feed',
  '9:16': 'Vertical · story & reel',
};

interface BannerGeometry {
  height: number;
  /** Inset of the badge/lockup row from the top of the hero. */
  padTop: number;
  /** Inset of the locality/price/spec stack from the bottom of the hero. */
  padBottom: number;
  /**
   * Scrim stops as [clearFrom, clearTo] percentages of the HERO — the
   * window in which the photograph reads unmodified. Outside it the
   * gradient ramps to the fixed alphas that keep white type legible.
   */
  clear: [number, number];
  /** Price size before auto-fit, and the floor it shrinks to. */
  price: [start: number, floor: number];
  /** Gallery rail. null renders a full-bleed hero instead. */
  rail: { pad: number; radius: number; tiles: number } | null;
}

// Two variants of the same design, because the two destinations impose
// opposite constraints on the SAME 9:16 canvas:
//
//   catalog — Meta CROPS it. Nothing overlays the creative, so the type
//             can sit near the edges and the extra height goes to a rail
//             of secondary photos, per the handoff.
//   social  — Meta OVERLAYS it. A story ad reserves the top 14% for the
//             profile row and the bottom 20% for the CTA, so anything in
//             those bands is simply not seen. The rail cannot survive the
//             bottom band at all.
//
// One table rather than two components: the corner-anchored composition,
// the type scale and the brand kit are identical, and only the numbers
// below move. Two components would drift.
export const BANNER_VARIANTS = ['catalog', 'social'] as const;
export type BannerVariant = (typeof BANNER_VARIANTS)[number];

export const BANNER_GEOMETRY: Record<
  BannerVariant,
  Record<BannerRatio, BannerGeometry>
> = {
  catalog: {
    '1:1': {
      height: 1080,
      padTop: 44,
      padBottom: 48,
      clear: [28, 34],
      price: [96, 56],
      rail: null,
    },
    '9:16': {
      height: 1920,
      padTop: 56,
      padBottom: 56,
      // Percentages of the hero, not the canvas. Tuned so the top band is
      // ~300px deep as in the square, and the bottom ramp is already dark
      // where the stack starts — legibility is a property of the distance
      // between the type and the photo, not of a share of the canvas.
      clear: [20, 52],
      price: [112, 64],
      rail: { pad: 40, radius: 20, tiles: 3 },
    },
  },
  // Currently an exact clone of `catalog` — on purpose. The variant exists
  // so the two destinations CAN diverge, not because they do yet; nothing
  // renders differently until someone changes a number here.
  //
  // What will want changing, when it does: a story ad reserves the top 14%
  // (269px) for the profile row and the bottom 20% (384px) for the CTA, so
  // `padTop`, `padBottom` and the rail are the three that matter. Switch
  // on "Safe zones" in the post preview to see what those bands cover.
  social: {
    '1:1': {
      height: 1080,
      padTop: 44,
      padBottom: 48,
      clear: [28, 34],
      price: [96, 56],
      rail: null,
    },
    '9:16': {
      height: 1920,
      padTop: 56,
      padBottom: 56,
      clear: [20, 52],
      price: [112, 64],
      rail: { pad: 40, radius: 20, tiles: 3 },
    },
  },
};

/** Canvas height for a ratio. 1080 for square, 1920 for vertical. */
export function bannerHeight(
  ratio: BannerRatio,
  variant: BannerVariant = 'catalog',
): number {
  return BANNER_GEOMETRY[variant][ratio].height;
}

/** How many secondary photos the rail needs. 0 when there is no rail. */
export function bannerGalleryCount(
  ratio: BannerRatio,
  variant: BannerVariant = 'catalog',
): number {
  return BANNER_GEOMETRY[variant][ratio].rail?.tiles ?? 0;
}

/**
 * Height of the photo area, which is what the QR position is normalised
 * against — not the canvas. With a rail the hero stops short of the
 * bottom, so a drag handle measured against the canvas drifts off the
 * code it is meant to sit on.
 *
 * `galleryCount` decides whether that listing actually gets a rail.
 */
export function bannerHeroHeight(
  ratio: BannerRatio,
  galleryCount = 0,
  variant: BannerVariant = 'catalog',
): number {
  const geo = BANNER_GEOMETRY[variant][ratio];
  const hasRail = !!geo.rail && galleryCount >= geo.rail.tiles;
  return railMetrics(hasRail ? geo : { ...geo, rail: null }).heroHeight;
}

/**
 * Rail and hero dimensions, derived from the pad — never hard-coded.
 *
 * The tiles are square BY CONSTRUCTION. Writing 306.67 as a literal is
 * what breaks the squareness the moment anyone touches the inset, so both
 * the tile size and the hero height fall out of `pad` here and nowhere
 * else computes them.
 */
function railMetrics(geo: BannerGeometry) {
  if (!geo.rail) return { tile: 0, heroHeight: geo.height };
  const { pad, tiles } = geo.rail;
  // pad on both outer edges, plus one pad in each gap between tiles.
  const tile = (BANNER_CANVAS - (tiles + 1) * pad) / tiles;
  return { tile, heroHeight: geo.height - pad - tile - pad };
}

// The handoff specifies a 44px mark beside a set-in-type wordmark. We show
// the mark alone, so it carries the lockup by itself and is sized to suit —
// tenant-controlled, since a wordmark-shaped logo needs more room than a
// square one. Radius and placeholder glyph derive from it so the
// proportions hold at any size.
export const MARK_DEFAULT = 88;
export const MARK_MIN = 24;
// Bottom-stack metrics, needed to place the QR's default position on the
// rule. Measured from the BOTTOM of the stack upward, which is why the
// price size doesn't enter into it — the rule's distance from the bottom
// edge is fixed no matter how far the price auto-fits down.
//
// Mirrors the JSX below: the stack's column gap, and the spec row's
// height (38px value + 8px gap + ~20px mono label).
const STACK_GAP = 28;
const SPEC_ROW_H = 38 + 8 + 20;
/** Horizontal inset of the stack — the rule spans between these. */
const STACK_PAD_X = 48;

// Permit QR. Must stay scannable — 120px on a 1080 canvas is ~11% of the
// frame, which survives Meta's downsampling. Below ~90 it starts failing.
export const QR_DEFAULT = 132;
export const QR_MIN = 96;
export const QR_MAX = 240;
/**
 * Default QR placement: sat down ON the rule, flush with its right end.
 *
 * Derived rather than a literal pair, because it has to hold as the
 * tenant resizes the code (96–240px) and across both canvases — the rule
 * sits a different distance down a 1:1 than a 9:16 hero. A frozen
 * fraction would drift off the line the moment either changed.
 *
 * Normalised against the HERO, which is what the template and the drag
 * handles both measure against. See lib/qrDrag.ts.
 */
export function bannerQrDefault(
  ratio: BannerRatio = '1:1',
  qrSize: number = QR_DEFAULT,
  variant: BannerVariant = 'catalog',
  galleryCount = 0,
): { x: number; y: number } {
  const geo = BANNER_GEOMETRY[variant][ratio];
  const heroH = bannerHeroHeight(ratio, galleryCount, variant);
  // Distance from the hero's lower edge up to the top of the rule.
  const ruleFromBottom = geo.padBottom + SPEC_ROW_H + STACK_GAP;
  // Sit the code the SAME distance off the rule as the price block does —
  // that distance is the stack's own column gap, so the two clear the line
  // by an equal amount and read as one row rather than one resting on it.
  return {
    x: (BANNER_CANVAS - STACK_PAD_X - qrSize) / BANNER_CANVAS,
    y: (heroH - ruleFromBottom - STACK_GAP - qrSize) / heroH,
  };
}

/** Back-compat constant: the square's default at the default QR size. */
export const QR_DEFAULT_POS = bannerQrDefault();
export const MARK_MAX = 200;
const markRadius = (size: number) => Math.round(size * 0.23);
const markGlyph = (size: number) => Math.round(size * 0.36);

export interface BannerBrand {
  /** Badge fill, logo mark, currency token. Needs ≥4.5:1 against white. */
  accent: string;
  /** Frame background — visible only if the photo fails or isn't square. */
  ink: string;
  /**
   * Base colour of the bottom scrim. The handoff fixes this at #06080C
   * and calls it load-bearing, so only the HUE is themable here — the
   * alpha ramp below is untouched, which is what keeps white type legible
   * over an arbitrary photo. A pale value will break that; validate it.
   */
  scrim: string;
  /** Text colour over imagery. Moving off white means re-tuning the scrim. */
  onPhoto: string;
  /** Canvas px. 0 for export, ~28 for in-app preview. */
  radius: number;
  logoUrl: string | null;
  /** Hides the lockup entirely when false. */
  showLogo: boolean;
  /** Mark size in canvas px. */
  logoSize: number;
  /** Regulatory QR. On by default — Dubai requires it on advertising. */
  showQr: boolean;
  /** QR size in canvas px. */
  qrSize: number;
  /**
   * Where the QR sits, as a fraction of the canvas (top-left corner of
   * the code, 0–1). Normalised rather than px so a saved position holds
   * at any export size. null uses the template's default placement.
   */
  qrPosition: { x: number; y: number } | null;
  fontDisplay: string;
  fontMono: string;
}

export interface BannerListing {
  imageUrl: string | null;
  imageAlt?: string;
  /**
   * Secondary photos for the 9:16 gallery rail. Ignored at 1:1.
   *
   * The rail is a fixed three-column composition, not a variable-length
   * carousel — it renders only when there are at least three, and a
   * listing short of that falls back to a full-bleed hero. Two tiles and
   * a hole is not a design the handoff has, and in an automated feed
   * "pick a different template" isn't an option the renderer has either.
   *
   * Feed square crops where you can: the tiles cover-crop as a safety
   * net, but a landscape source loses its edges to it.
   */
  gallery?: string[] | null;
  /** JUST LISTED / PRICE REDUCED / … null hides the pill entirely. */
  badge: string | null;
  locality: string;
  city: string | null;
  /** NUMERIC. A preformatted string defeats locale grouping and auto-fit. */
  price: number | null;
  currency: string;
  /** BCP 47. Drives grouping, symbol position, and RTL. */
  locale: string;
  /** STRINGS, not numbers — feeds carry "4 + maid", "Studio", "6+". */
  beds: string | null;
  baths: string | null;
  area: number | null;
  areaUnit: 'sqft' | 'sqm';
  /**
   * Hosted PNG of the Trakheesi permit QR. Dubai requires it on
   * advertised creative, so it renders whenever supplied and
   * `brand.showQr` is on. null simply omits it.
   */
  permitQr: string | null;
}

export const DEFAULT_BRAND: BannerBrand = {
  accent: '#408AF7',
  ink: '#0B0B0C',
  scrim: '#06080C',
  onPhoto: '#FFFFFF',
  radius: 0,
  logoUrl: null,
  showLogo: true,
  logoSize: MARK_DEFAULT,
  showQr: true,
  qrSize: QR_DEFAULT,
  qrPosition: null,
  fontDisplay: "'Jost', system-ui, sans-serif",
  fontMono: "'Geist Mono', ui-monospace, Menlo, monospace",
};

/** '#06080C' → '6,8,12'. Falls back to the handoff's value on bad input. */
function rgbOf(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m?.[1]) return '6,8,12';
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}

const AREA_LABEL: Record<BannerListing['areaUnit'], string> = {
  sqft: 'SQ FT',
  sqm: 'SQ M',
};

// Split a price into its currency token and grouped digits, per locale, so
// AED 8,500,000 / 1.250.000 € / £2,400,000 all come from one code path.
// `symbolFirst` says whether the token leads — never assume a prefix.
function formatPrice(amount: number, currency: string, locale: string) {
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(amount);
    const symbol = parts
      .filter((p) => p.type === 'currency')
      .map((p) => p.value)
      .join('');
    const digits = parts
      .filter((p) => p.type !== 'currency' && p.type !== 'literal')
      .map((p) => p.value)
      .join('');
    return {
      symbol: symbol || currency,
      digits,
      symbolFirst: parts.findIndex((p) => p.type === 'currency') === 0,
    };
  } catch {
    // Unknown currency code — fall back rather than blank the price.
    return {
      symbol: currency,
      digits: String(Math.round(amount)),
      symbolFirst: true,
    };
  }
}

export function ListingBanner({
  brand,
  listing,
  /** Rendered width in CSS px. The 1080 canvas scales to fit it. */
  width = BANNER_CANVAS,
  /** Canvas shape. Square for the feed, vertical for stories and reels. */
  ratio = '1:1',
  /** Which destination's constraints apply. See BANNER_GEOMETRY. */
  variant = 'catalog',
  className,
}: {
  brand: BannerBrand;
  listing: BannerListing;
  width?: number;
  ratio?: BannerRatio;
  variant?: BannerVariant;
  className?: string;
}) {
  const geo = BANNER_GEOMETRY[variant][ratio];
  const scale = width / BANNER_CANVAS;
  const rtl = listing.locale.startsWith('ar');

  // Exactly the tiles the rail needs, or none. A partial rail is not a
  // state this design has — see BannerListing.gallery.
  const gallery = (listing.gallery ?? []).filter(Boolean);
  const rail =
    geo.rail && gallery.length >= geo.rail.tiles ? geo.rail : null;
  const { tile, heroHeight } = railMetrics(rail ? geo : { ...geo, rail: null });
  const qrDefault = bannerQrDefault(
    ratio,
    brand.qrSize,
    variant,
    gallery.length,
  );

  const priceRowRef = React.useRef<HTMLDivElement>(null);
  const amountRef = React.useRef<HTMLSpanElement>(null);
  const [amountSize, setAmountSize] = React.useState(geo.price[0]);

  const price =
    listing.price == null
      ? null
      : formatPrice(listing.price, listing.currency, listing.locale);

  // The price is the one field that can legitimately overflow — a nine
  // figure listing, or a long symbol. Shrink in 2px steps to the ratio's
  // floor rather than wrap or clip. Must run after the webfont lands;
  // measuring before gives the fallback font's width.
  //
  // The horizontal budget is the same in both ratios (the canvas is 1080
  // wide either way), but the start and floor sizes differ, so re-run when
  // the ratio changes or a 9:16 price keeps the square's fitted size.
  React.useLayoutEffect(() => {
    let cancelled = false;
    const [start, floor] = geo.price;
    const fit = () => {
      const row = priceRowRef.current;
      const amt = amountRef.current;
      if (cancelled || !row || !amt) return;
      const avail = BANNER_CANVAS - 48 * 2; // .bottom padding
      let size = start;
      amt.style.fontSize = `${size}px`;
      while (row.scrollWidth > avail && size > floor) {
        size -= 2;
        amt.style.fontSize = `${size}px`;
      }
      setAmountSize(size);
    };
    fit();
    void document.fonts?.ready.then(fit);
    return () => {
      cancelled = true;
    };
  }, [
    listing.price,
    listing.currency,
    listing.locale,
    brand.fontDisplay,
    geo.price,
  ]);

  const specs: { value: string; label: string }[] = [];
  // A null spec omits its column entirely — never an empty one or a dash.
  if (listing.beds) specs.push({ value: listing.beds, label: 'BEDROOMS' });
  if (listing.baths) specs.push({ value: listing.baths, label: 'BATHROOMS' });
  if (listing.area != null) {
    specs.push({
      value: new Intl.NumberFormat(listing.locale, {
        maximumFractionDigits: 0,
      }).format(listing.area),
      label: AREA_LABEL[listing.areaUnit] ?? 'SQ FT',
    });
  }

  const place = [listing.locality, listing.city]
    .filter(Boolean)
    .join(' · ')
    .toUpperCase();

  return (
    // Outer box occupies the scaled footprint; the canvas inside is always
    // 1080 and transformed down.
    <div
      className={className}
      style={{
        width,
        height: width * (geo.height / BANNER_CANVAS),
        overflow: 'hidden',
      }}
    >
      <div
        dir={rtl ? 'rtl' : undefined}
        style={{
          position: 'relative',
          width: BANNER_CANVAS,
          height: geo.height,
          overflow: 'hidden',
          background: brand.ink,
          borderRadius: brand.radius,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          fontSynthesis: 'none',
          WebkitFontSmoothing: 'antialiased',
        }}
      >
        {/* Hero. The whole corner-anchored composition lives in here, so
            it is identical in both ratios — at 1:1 it IS the canvas, and
            with a rail it stops short of it and takes rounded bottom
            corners to meet the tiles' radius. Every absolutely-positioned
            child below anchors to this box, not to the canvas, which is
            what stops the price sliding down over the rail. */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: BANNER_CANVAS,
            height: heroHeight,
            overflow: 'hidden',
            // Bleeds off the top edge; only the bottom corners round.
            borderRadius: rail ? `0 0 ${rail.radius}px ${rail.radius}px` : 0,
          }}
        >
        {listing.imageUrl && (
          <img
            src={listing.imageUrl}
            alt={listing.imageAlt ?? ''}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        )}

        {/* Scrim is load-bearing: it is the only thing guaranteeing the
            white type stays legible over an arbitrary agent photo. Never
            conditional, never softened. */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            // Alphas are verbatim from the handoff and fixed across both
            // ratios; only the base colour and the clear window move. The
            // window is per-ratio because a percentage means a different
            // number of pixels on a 1920 canvas than on a 1080 one.
            background: `linear-gradient(180deg,rgba(${rgbOf(
              brand.scrim,
            )},.60) 0%,rgba(${rgbOf(brand.scrim)},0) ${
              geo.clear[0]
            }%,rgba(${rgbOf(brand.scrim)},0) ${geo.clear[1]}%,rgba(${rgbOf(
              brand.scrim,
            )},.90) 100%)`,
          }}
        />

        {/* Top row — badge + agency lockup.
            Aligned to the top, not centred: the row's height follows the
            logo, so centring would slide the badge down as the logo grows.
            Both now hang from a fixed 44px inset and the logo extends
            downward on its own. */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            // Horizontal inset is shared; the vertical one drops the row
            // clear of the Stories/Reels profile overlay on 9:16.
            padding: `${geo.padTop}px 44px`,
          }}
        >
          {listing.badge ? (
            <span
              style={{
                background: brand.accent,
                color: '#fff',
                font: `600 18px ${brand.fontMono}`,
                letterSpacing: '.2em',
                padding: '16px 26px',
                borderRadius: 999,
                whiteSpace: 'nowrap',
              }}
            >
              {listing.badge}
            </span>
          ) : (
            // Empty spacer keeps the lockup right-aligned.
            <span />
          )}

          {brand.showLogo && (
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              {brand.logoUrl ? (
                // Sized by HEIGHT, not boxed in a square. A wordmark logo
                // centred inside a growing square appears to slide down as
                // it scales; anchoring on height means the mark grows
                // downward and leftward from a fixed top-right corner.
                <img
                  src={brand.logoUrl}
                  alt=""
                  style={{
                    height: brand.logoSize,
                    width: 'auto',
                    maxWidth: 520,
                    objectFit: 'contain',
                    objectPosition: 'top right',
                    display: 'block',
                  }}
                />
              ) : (
                // Placeholder keeps the handoff's accent tile — it has no
                // intrinsic aspect ratio to preserve.
                <div
                  style={{
                    width: brand.logoSize,
                    height: brand.logoSize,
                    borderRadius: markRadius(brand.logoSize),
                    background: brand.accent,
                    display: 'grid',
                    placeItems: 'center',
                    flex: 'none',
                  }}
                >
                  <div
                    style={{
                      width: markGlyph(brand.logoSize),
                      height: markGlyph(brand.logoSize),
                      background: '#fff',
                      transform: 'rotate(45deg)',
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Regulatory permit QR. Sits on the right, above the bottom
            stack, so it never collides with the price or the spec row.
            White plate because a QR needs quiet-zone contrast to scan —
            reading reliably matters more than the plate-free look here. */}
        {brand.showQr && listing.permitQr && (
          <img
            src={listing.permitQr}
            alt=""
            style={{
              position: 'absolute',
              // Dragged position wins; otherwise the designed placement.
              // Normalised against the HERO, not the canvas. The QR lives
              // on the photograph in both ratios, so a saved position has
              // to land in the same spot relative to the image — against
              // the full 1920 canvas it would drift down onto the rail.
              //
              // The fallback is computed per ratio and per QR size, so an
              // untouched code sits on the rule in both canvases.
              left: (brand.qrPosition ?? qrDefault).x * BANNER_CANVAS,
              top: (brand.qrPosition ?? qrDefault).y * heroHeight,
              width: brand.qrSize,
              height: brand.qrSize,
              borderRadius: 8,
              background: '#fff',
              padding: 8,
              boxSizing: 'border-box',
              display: 'block',
            }}
          />
        )}

        {/* Bottom stack — locality, price, rule, specs */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            // Top padding is inert on a bottom-anchored block; the bottom
            // value is the one that matters, and on 9:16 it lifts the
            // stack above the full-screen CTA.
            padding: `0 48px ${geo.padBottom}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: 28,
          }}
        >
          <span
            style={{
              font: `500 22px ${brand.fontMono}`,
              letterSpacing: '.18em',
              color: 'rgba(255,255,255,.72)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {place}
          </span>

          {price && (
            <div
              ref={priceRowRef}
              style={{ display: 'flex', alignItems: 'flex-end', gap: 20 }}
            >
              {price.symbolFirst && (
                <span
                  style={{
                    font: `500 28px ${brand.fontMono}`,
                    color: brand.accent,
                    paddingBottom: 12,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {price.symbol}
                </span>
              )}
              <span
                ref={amountRef}
                style={{
                  font: `600 ${amountSize}px/.86 ${brand.fontDisplay}`,
                  letterSpacing: '-.035em',
                  color: brand.onPhoto,
                  whiteSpace: 'nowrap',
                  // Digits stay LTR even when the canvas mirrors.
                  ...(rtl
                    ? { direction: 'ltr' as const, unicodeBidi: 'embed' as const }
                    : {}),
                }}
              >
                {price.digits}
              </span>
              {!price.symbolFirst && (
                <span
                  style={{
                    font: `500 28px ${brand.fontMono}`,
                    color: brand.accent,
                    paddingBottom: 12,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {price.symbol}
                </span>
              )}
            </div>
          )}

          <div style={{ height: 2, background: 'rgba(255,255,255,.24)' }} />

          <div style={{ display: 'flex', gap: 76 }}>
            {specs.map((s) => (
              <div
                key={s.label}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    font: `500 38px/1 ${brand.fontDisplay}`,
                    color: brand.onPhoto,
                    whiteSpace: 'nowrap',
                    ...(rtl
                      ? {
                          direction: 'ltr' as const,
                          unicodeBidi: 'embed' as const,
                        }
                      : {}),
                  }}
                >
                  {s.value}
                </span>
                <span
                  style={{
                    font: `500 17px ${brand.fontMono}`,
                    letterSpacing: '.16em',
                    color: 'rgba(255,255,255,.55)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>
        </div>

        {/* Gallery rail. Three square tiles floating on the ink ground,
            inset from the left, right and bottom by exactly the same pad
            that separates them — one value governs both, so the rail
            reads as an even field rather than a strip.

            The tile size comes from railMetrics(); nothing here may
            hard-code it. Squareness is the whole point of the composition
            and a literal is what quietly breaks it. */}
        {rail && (
          <div
            style={{
              position: 'absolute',
              left: rail.pad,
              right: rail.pad,
              bottom: rail.pad,
              display: 'flex',
              gap: rail.pad,
            }}
          >
            {gallery.slice(0, rail.tiles).map((src, i) => (
              <img
                key={`${src}-${i}`}
                src={src}
                alt=""
                style={{
                  width: tile,
                  height: tile,
                  borderRadius: rail.radius,
                  // Safety net only — feed square crops. A landscape
                  // source centre-cropped to a square loses its edges.
                  objectFit: 'cover',
                  display: 'block',
                  flex: 'none',
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
